// Content for the Compteurs d'eau ATNA hero.
// Dashboard values are illustrative demo data, shown only inside the interface mockup.
export type Bi = { fr: string; en: string }

export const WATER_HERO_IMAGES = {
  background: 'https://cdn.magicpatterns.com/patterns/generated-images/b06aab60-2393-4079-a664-78a8b6936b46.jpg',
  meter: 'https://cdn.magicpatterns.com/patterns/generated-images/6e399eae-bd89-43e2-9796-c64d50dfa5cd.jpg',
  house: 'https://cdn.magicpatterns.com/patterns/generated-images/9da5a5b4-398d-4b17-a8c6-dd221fc71260.jpg',
}

export const waterHeroCopy = {
  eyebrow: { fr: 'Compteurs d’eau ATNA', en: 'ATNA Smart Water' },
  navy: [{ fr: 'L’eau sous', en: 'Water under' }, { fr: 'contrôle.', en: 'control.' }],
  indigo: [{ fr: 'Des actions', en: 'Concrete' }, { fr: 'concrètes.', en: 'action.' }],
  body: {
    fr: 'Des données en temps réel, reliées aux bonnes personnes, pour détecter les fuites, maîtriser la consommation et agir plus rapidement.',
    en: 'Real-time data, connected to the right people, to detect leaks, manage consumption and act faster.',
  },
  primary: { fr: 'Demander une démo', en: 'Request a demo' },
  secondary: { fr: 'Voir en production', en: 'See it in production' },
  alertTitle: { fr: 'Fuite détectée', en: 'Leak detected' },
  alertSub: { fr: 'Consommation anormale', en: 'Abnormal consumption' },
  alertAddress: { fr: '123 rue Principale', en: '123 Main Street' },
  houseAlt: { fr: 'Propriété reliée au compteur', en: 'Property linked to the meter' },
  meterAlt: { fr: 'Compteur d’eau connecté', en: 'Connected water meter' },
}

export const waterDashboard = {
  search: { fr: 'Rechercher une adresse, un compteur, un propriétaire…', en: 'Search by address, meter or owner…' },
  territory: { fr: 'Tous les territoires', en: 'All territories' },
  title: { fr: 'Aperçu des compteurs d’eau', en: 'Water meter overview' },
  live: { fr: 'Données mises à jour en temps réel', en: 'Data updated in real time' },
  period: { fr: 'Cette semaine', en: 'This week' },
  nav: [
    { key: 'overview', label: { fr: 'Aperçu', en: 'Overview' } },
    { key: 'properties', label: { fr: 'Propriétés', en: 'Properties' } },
    { key: 'meters', label: { fr: 'Compteurs', en: 'Meters' } },
    { key: 'consumption', label: { fr: 'Consommation', en: 'Consumption' } },
    { key: 'alerts', label: { fr: 'Alertes', en: 'Alerts' } },
    { key: 'interventions', label: { fr: 'Interventions', en: 'Work orders' } },
    { key: 'reports', label: { fr: 'Rapports', en: 'Reports' } },
  ] as { key: 'overview' | 'properties' | 'meters' | 'consumption' | 'alerts' | 'interventions' | 'reports'; label: Bi }[],
  kpis: [
    { key: 'meters', value: { fr: '12 842', en: '12,842' }, label: { fr: 'Compteurs actifs', en: 'Active meters' }, trend: { fr: '↑ +3 %', en: '↑ +3%' }, tone: 'good' },
    { key: 'leaks', value: { fr: '27', en: '27' }, label: { fr: 'Alertes fuites', en: 'Leak alerts' }, trend: { fr: '↑ +5 %', en: '↑ +5%' }, tone: 'bad' },
    { key: 'linked', value: { fr: '98 %', en: '98%' }, label: { fr: 'Propriétés liées et vérifiées', en: 'Properties linked & verified' }, trend: null, tone: 'none' },
    { key: 'down', value: { fr: '18 %', en: '18%' }, label: { fr: 'Consommation en baisse', en: 'Lower consumption' }, trend: { fr: '↓', en: '↓' }, tone: 'good' },
  ] as { key: 'meters' | 'leaks' | 'linked' | 'down'; value: Bi; label: Bi; trend: Bi | null; tone: 'good' | 'bad' | 'none' }[],
  dailyTitle: { fr: 'Consommation quotidienne', en: 'Daily consumption' },
  days: { fr: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'], en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
  daily: [190, 240, 235, 280, 270, 300, 350],
  sectorTitle: { fr: 'Répartition par type d’usage', en: 'Breakdown by customer type' },
  donutCenter: { fr: 'compteurs', en: 'meters' },
  sectors: [
    { key: 'res', pct: 68, color: '#4F6BF0', label: { fr: 'Résidentiel', en: 'Residential' } },
    { key: 'com', pct: 18, color: '#B04FD8', label: { fr: 'Commercial', en: 'Commercial' } },
    { key: 'ind', pct: 9, color: '#E9A79A', label: { fr: 'Industriel', en: 'Industrial' } },
    { key: 'ins', pct: 5, color: '#B9C8F5', label: { fr: 'Institutionnel', en: 'Institutional' } },
  ],
}

export const waterJourney: { key: 'register' | 'measure' | 'detect' | 'act'; label: Bi; text: Bi }[] = [
  { key: 'register', label: { fr: 'Enregistrer', en: 'Register' }, text: { fr: 'Compteurs et propriétés reliés aux bons usagers.', en: 'Meters and properties linked to the right users.' } },
  { key: 'measure', label: { fr: 'Mesurer', en: 'Measure' }, text: { fr: 'Consommation visible et contextualisée.', en: 'Consumption made visible, in context.' } },
  { key: 'detect', label: { fr: 'Détecter', en: 'Detect' }, text: { fr: 'Anomalies et fuites identifiées rapidement.', en: 'Anomalies and leaks identified quickly.' } },
  { key: 'act', label: { fr: 'Agir', en: 'Act' }, text: { fr: 'Alertes, interventions et suivis proactifs.', en: 'Alerts, work orders and proactive follow-up.' } },
]
